import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  BrowserRouter as Router,
  useLocation,
  useNavigationType,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import AppRoutes from "./app/AppRoutes";
import { useAnalyticsSession } from "./analytics/sessionTracking";
import { trackProductEvent } from "./analytics/trackEvent";
import { getAdminAnalyticsAccess } from "./api/adminAnalytics";
import useFishingOutlookBetaAccess, {
  FISHING_OUTLOOK_ACCESS_STATE,
} from "./features/fishingOutlook/useFishingOutlookBetaAccess";
import RouteSeo from "./seo/RouteSeo";
import {
  buildRootTabSearch,
  getRootTabFromSearch,
  normalizeRootTab,
} from "./app/navigationState";
import { allowMarketTab } from "./config/featureVisibility";
import useAndroidRootExitGuard from "./app/useAndroidRootExitGuard";
import useAppOverlayHistory from "./app/useAppOverlayHistory";
import { buildLocationStackState } from "./app/locationStateStackLogic";
import useAppSessionState from "./app/useAppSessionState";
import useMessagingUiState from "./app/useMessagingUiState";
import usePostsAndComposerState from "./app/usePostsAndComposerState";
import PushRegistrationLifecycleProvider from "./push/PushRegistrationLifecycleProvider";
import PushNotificationOnboarding from "./notifications/onboarding/PushNotificationOnboarding";
import {
  continueFishipediaRestoreHistory,
  isFishipediaHistoryTransitionPending,
  submitFishipediaRestoreHistory,
} from "./app/fishipediaRestoreHistory";
import {
  buildFishipediaRestoreHistoryStates,
  claimFishipediaRestoreAttempt,
  createPostInfoFishipediaReturnContext,
  FISHIPEDIA_RETURN_CONTEXT_KEY,
  getFishipediaRestoreContext,
  resolveFishipediaPostRestoreReadiness,
} from "./app/fishipediaReturnContext";
import useOrdinaryPostLikeMutation, {
  useClearOrdinaryPostLikeErrorsOnUserChange,
} from "./hooks/useOrdinaryPostLikeMutation";
import {
  CLEAR_MAP_SELECTION_EVENT,
  MapStateManager,
} from "./utils/MapStateManager";
import AppStartupOverlay from "./components/AppStartupOverlay";
import CreatePostModal from "./components/CreatePostModal/CreatePostModal";
import FeedDesktopRightPanel from "./components/FeedDesktopRightPanel";
import FeedMobileBottomNav from "./components/FeedMobileBottomNav";
import InfoPopup from "./components/InfoPopup";
import LegalAcceptanceModal from "./components/LegalAcceptanceModal";
import LevelUpModal from "./components/LevelUpModal";
import NavBanner from "./components/NavBanner";
import Profile from "./components/Profile";
import SharePostModal from "./components/SharePostModal";
import Sidebar from "./components/Sidebar";
import UserProfile from "./components/UserProfile";
import {
  APP_MODES,
  getInitialAppMode,
  isSimpleMode,
  setStoredAppMode,
} from "./utils/appMode";
import {
  applyThemeMode,
  getInitialThemeMode,
  setStoredThemeMode,
} from "./utils/themeMode";
import "./styles/App.css";
import "./styles/components/CreatePost.css";
import "./styles/components/InfoPopup.css";
import "./styles/components/Modal.css";
import "./styles/components/Post.css";
import "./styles/components/ReelInbox.css";
import "./styles/layout/Feed.css";
import "./styles/layout/Navbar.css";
import "./styles/layout/reelitpro-shell/index.css";

const MAP_RIGHT_PANEL_HOST_ID = "rp-map-rightpanel-host";
const TRIPS_RIGHT_PANEL_HOST_ID = "rp-trips-rightpanel-host";
const UTILITY_RIGHT_PANEL_HOST_ID = "rp-utility-rightpanel-host";
const REFRESH_APP_CHROME_METRICS_EVENT = "reelitpro:refresh-app-chrome-metrics";
const MODERN_PAGE_SHELL_ROUTES = new Set([
  "/fishing-today",
  "/favorite-spots",
  "/my-gear",
  "/suggested-anglers",
  "/fishipedia",
  "/fishipedia/rankings",
  "/regulations",
  "/feedback",
  "/manual",
  "/surveys",
  "/support",
  "/support-app",
  "/trips",
  "/messages",
  "/admin/analytics",
  "/records",
  "/news",
  "/school",
  "/legal/terms",
  "/legal/privacy",
  "/legal/account-deletion",
  "/legal/attributions",
  "/migration/link-supabase",
  "/fishing-app",
  "/fishing-catch-log-app",
  "/fishing-spots-app",
  "/fishing-trip-planner",
  "/fish-species-guide",
  "/fishing-regulations",
]);
const MOBILE_SEARCH_HIDDEN_ROUTES = new Set([
  "/fishing-today",
  "/favorite-spots",
  "/fishipedia",
  "/fishipedia/rankings",
  "/manual",
  "/my-gear",
  "/support-app",
  "/surveys",
  "/trips",
]);

const PAGE_OPEN_ANALYTICS_BY_PATH = {
  "/fishing-today": ["fishing_today_opened", "fishing_today"],
  "/trips": ["my_trips_opened", "trips"],
  "/favorite-spots": ["favorite_spots_opened", "favorite_spots"],
  "/suggested-anglers": ["suggested_anglers_opened", "suggested_anglers"],
  "/fishipedia": ["fishipedia_opened", "fishipedia"],
  "/fishipedia/rankings": ["fishipedia_rankings_opened", "fishipedia"],
  "/regulations": ["regulations_opened", "regulations"],
  "/feedback": ["feedback_opened", "feedback"],
  "/manual": ["manual_opened", "manual"],
  "/surveys": ["surveys_opened", "surveys"],
  "/support": ["support_opened", "support"],
  "/support-app": ["support_app_opened", "support_app"],
  "/messages": ["messages_opened", "messages"],
  "/records": ["records_opened", "records"],
  "/news": ["news_opened", "news"],
  "/school": ["school_opened", "school"],
  "/my-gear": ["my_gear_opened", "my_gear"],
};

const ROOT_TAB_OPEN_ANALYTICS = {
  feed: ["feed_opened", "feed"],
  map: ["map_opened", "map"],
  groups: ["groups_opened", "groups"],
  market: ["market_opened", "market"],
};

function normalizeCoords(str) {
  if (!str) return "";
  return str.replace(/\s+/g, "").trim();
}

function removeTripsOverlayStackFromState(locationState) {
  if (!locationState || typeof locationState !== "object") {
    return locationState ?? null;
  }

  const nextState = { ...locationState };
  delete nextState.tripsOverlayStack;

  return Object.keys(nextState).length > 0 ? nextState : null;
}

const FISHIPEDIA_TARGET_KEYS = new Set([
  "anchor",
  "entry",
  "fish",
  "fishSlug",
  "fishipediaSlug",
  "scrollTarget",
  "slug",
  "species",
  "speciesId",
  "speciesName",
  "speciesSlug",
  "target",
  "targetSpecies",
]);

function PostLikeErrorToast({ userId }) {
  const { t } = useTranslation();
  const { latestPostLikeError, dismissPostLikeError } =
    useOrdinaryPostLikeMutation();
  useClearOrdinaryPostLikeErrorsOnUserChange(userId);

  if (!latestPostLikeError) return null;

  return (
    <div className="root-exit-toast post-like-error-toast" role="alert">
      <span>{latestPostLikeError.message}</span>
      <button
        type="button"
        className="post-like-error-toast__dismiss"
        aria-label={t("common_close", { defaultValue: "Close" })}
        onClick={() => dismissPostLikeError(latestPostLikeError.postId)}
      >
        <X size={18} aria-hidden="true" />
      </button>
    </div>
  );
}

function hasFishipediaSearchTarget(search) {
  if (!search) return false;

  const params = new URLSearchParams(search);
  for (const key of FISHIPEDIA_TARGET_KEYS) {
    if (params.has(key)) return true;
  }

  return false;
}

function hasFishipediaStateTarget(state) {
  if (!state || typeof state !== "object") return false;

  return Object.keys(state).some((key) => FISHIPEDIA_TARGET_KEYS.has(key));
}

function shouldSkipAutomaticScrollReset(location) {
  if (location.hash) return true;

  if (location.pathname !== "/fishipedia") return false;

  return (
    hasFishipediaSearchTarget(location.search) ||
    hasFishipediaStateTarget(location.state)
  );
}

function resetElementScroll(element) {
  if (!element) return;

  try {
    if (typeof element.scrollTo === "function") {
      element.scrollTo({ top: 0, left: 0, behavior: "auto" });
      return;
    }
  } catch {
    // Fall through to direct scroll offsets for older element implementations.
  }

  if ("scrollTop" in element) element.scrollTop = 0;
  if ("scrollLeft" in element) element.scrollLeft = 0;
}

function resetAppScrollContainers() {
  if (typeof window === "undefined") return;

  const doc = window.document;
  const scrollingElement = doc.scrollingElement || doc.documentElement;

  try {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  } catch {
    window.scrollTo(0, 0);
  }

  resetElementScroll(scrollingElement);
  resetElementScroll(doc.documentElement);
  resetElementScroll(doc.body);

  [
    ".app-container",
    ".rp-desktop__main",
    ".profile-overlay",
    ".userprofile-container",
  ].forEach((selector) => {
    doc.querySelectorAll(selector).forEach(resetElementScroll);
  });
}

function AppShell() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();

  useLayoutEffect(() => {
    continueFishipediaRestoreHistory(navigate, location, navigationType);
  }, [location, navigate, navigationType]);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const [activeTabState, setActiveTabState] = useState(() =>
    location.pathname === "/" ? getRootTabFromSearch(location.search) : "feed"
  );
  const [showMenu, setShowMenu] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showDeleteAccountConfirm, setShowDeleteAccountConfirm] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState("");
  const [mapRightPanelActive, setMapRightPanelActive] = useState(false);
  const [mapLocateMeHandler, setMapLocateMeHandler] = useState(null);
  const [isCatchMarkerDetailsOpen, setIsCatchMarkerDetailsOpen] = useState(false);
  const [canViewAdminAnalytics, setCanViewAdminAnalytics] = useState(false);
  const [appMode, setAppMode] = useState(getInitialAppMode);
  const [themeMode, setThemeMode] = useState(getInitialThemeMode);
  const activeTabRef = useRef(activeTabState);
  const lastTrackedPageEventRef = useRef("");
  const profileOpenTrackedRef = useRef(false);
  const profileOpenPagePathOverrideRef = useRef("");
  const suppressPickerOpenNavigationRef = useRef(() => {});
  const suppressPickerReturnNavigationRef = useRef(() => {});
  const overlayHistoryRef = useRef(null);
  const consumedFishipediaRestoreKeyRef = useRef("");

  useEffect(() => {
    activeTabRef.current = activeTabState;
  }, [activeTabState]);

  const simpleModeActive = isSimpleMode(appMode);

  useEffect(() => {
    setStoredAppMode(appMode);
  }, [appMode]);

  useEffect(() => {
    setStoredThemeMode(themeMode);
    applyThemeMode(themeMode);
  }, [themeMode]);

  const handleAppModeChange = useCallback((nextAppMode) => {
    if (nextAppMode === APP_MODES.SIMPLE || nextAppMode === APP_MODES.FULL) {
      setAppMode(nextAppMode);
    }
  }, []);

  const handleSwitchToFullMode = useCallback(() => {
    setAppMode(APP_MODES.FULL);
  }, []);

  const handleThemeModeChange = useCallback((nextThemeMode) => {
    setThemeMode(nextThemeMode);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => {
      setIsMobileViewport(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  useEffect(() => {
    if (location.pathname !== "/") {
      return;
    }

    if (!allowMarketTab) {
      const params = new URLSearchParams(location.search);
      if (String(params.get("tab") || "").trim().toLowerCase() === "market") {
        params.delete("tab");
        const nextSearch = params.toString();
        navigate(
          {
            pathname: "/",
            search: nextSearch ? `?${nextSearch}` : "",
            hash: location.hash || "",
          },
          {
            replace: true,
            state: location.state,
          }
        );
        return;
      }
    }

    const nextTab = getRootTabFromSearch(location.search);
    setActiveTabState((currentTab) =>
      currentTab === nextTab ? currentTab : nextTab
    );
  }, [location.hash, location.pathname, location.search, location.state, navigate]);

  const setActiveTab = useCallback(
    (nextValue) => {
      const currentTab = activeTabRef.current;
      const resolvedValue =
        typeof nextValue === "function" ? nextValue(currentTab) : nextValue;
      const nextTab = normalizeRootTab(resolvedValue);
      const nextSearch = buildRootTabSearch(location.search, nextTab);

      activeTabRef.current = nextTab;
      setActiveTabState((current) => (current === nextTab ? current : nextTab));

      if (
        location.pathname !== "/" ||
        location.search !== nextSearch ||
        location.hash
      ) {
        const nextState =
          location.pathname === "/trips"
            ? removeTripsOverlayStackFromState(location.state)
            : location.state;

        navigate(
          {
            pathname: "/",
            search: nextSearch,
            hash: "",
          },
          {
            state: nextState,
          }
        );
      }
    },
    [location.hash, location.pathname, location.search, location.state, navigate]
  );

  const forcePickerReturnToFeed = useCallback((source = "") => {
    if (source === "pastTripCatch") {
      activeTabRef.current = "feed";
      setActiveTabState("feed");
      navigate(
        {
          pathname: "/trips",
          search: "",
          hash: "",
        },
        {
          replace: true,
          state: removeTripsOverlayStackFromState(location.state),
        }
      );
      return;
    }

    const feedLocation = {
      pathname: "/",
      search: buildRootTabSearch("", "feed"),
      hash: "",
    };

    activeTabRef.current = "feed";
    setActiveTabState("feed");
    navigate(feedLocation, {
      replace: true,
      state: location.state,
    });

    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        activeTabRef.current = "feed";
        setActiveTabState("feed");
        navigate(feedLocation, {
          replace: true,
          state: location.state,
        });
      }, 0);
    }
  }, [location.state, navigate]);

  const activeTab = location.pathname === "/"
    ? getRootTabFromSearch(location.search)
    : activeTabState;
  const isFeedRoute = location.pathname === "/" && activeTab === "feed";
  const pendingRouteFishipediaRestore = getFishipediaRestoreContext(
    location.state
  );
  const shouldResolvePostsForRoute =
    isFeedRoute ||
    (location.pathname === "/" &&
      pendingRouteFishipediaRestore?.type === "post-info");

  const messaging = useMessagingUiState();
  const pushLifecycleRef = useRef(null);
  const session = useAppSessionState({
    activeTab,
    setActiveTab,
    setShowMenu,
    pushLifecycleRef,
  });
  const fishingOutlookAccessState = useFishingOutlookBetaAccess(
    session.user?.id
  );
  const canAccessFishingOutlook =
    fishingOutlookAccessState === FISHING_OUTLOOK_ACCESS_STATE.AUTHORIZED;
  useAnalyticsSession({ userId: session.user?.id });
  const postsComposer = usePostsAndComposerState({
    user: session.user,
    setUser: session.setUser,
    setActiveTab,
    profileUi: session.profileUi,
    onSuppressPickerOpenNavigation: () => {
      suppressPickerOpenNavigationRef.current?.();
    },
    onSuppressPickerReturnNavigation: () => {
      suppressPickerReturnNavigationRef.current?.();
    },
    onForcePickerReturnNavigation: forcePickerReturnToFeed,
    authTransitioning: session.isAuthTransitioning,
    isFeedRoute: shouldResolvePostsForRoute,
    authoritativeRestoreKey:
      pendingRouteFishipediaRestore?.type === "post-info"
        ? pendingRouteFishipediaRestore.restoreAttemptId
        : "",
    authoritativeRestorePostId:
      pendingRouteFishipediaRestore?.type === "post-info"
        ? pendingRouteFishipediaRestore.postId
        : "",
  });

  useEffect(() => {
    if (location.pathname !== "/") return;

    const restoreContext = getFishipediaRestoreContext(location.state);
    if (
      restoreContext?.type !== "post-info" ||
      restoreContext.surface !== "app"
    ) {
      return;
    }

    if (
      consumedFishipediaRestoreKeyRef.current ===
      restoreContext.restoreAttemptId
    ) {
      return;
    }

    const restoreReadiness = resolveFishipediaPostRestoreReadiness(
      postsComposer.startup.authoritativePostRestoreResult,
      restoreContext
    );
    if (restoreReadiness.status === "pending") return;

    const historyStates = buildFishipediaRestoreHistoryStates(location.state);
    if (!historyStates || !claimFishipediaRestoreAttempt(restoreContext)) {
      return;
    }

    consumedFishipediaRestoreKeyRef.current = restoreContext.restoreAttemptId;
    const outcome =
      restoreReadiness.status === "found" ? "success" : "terminal";
    const transition = submitFishipediaRestoreHistory({
      navigate,
      location,
      states: historyStates,
      outcome,
    });
    if (transition === "fallback" || transition === "ignored") {
      postsComposer.info.setShowInfo(false);
      return;
    }

    if (restoreReadiness.status === "found") {
      postsComposer.info.setSelectedPost(restoreReadiness.post);
      postsComposer.info.setShowInfo(true);
    }
  }, [
    location,
    location.key,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
    postsComposer.info,
    postsComposer.startup.authoritativePostRestoreResult,
  ]);
  const shouldShowLoggedInStartupOverlay =
    session.isBootstrappingStoredSession &&
    (!session.authResolved ||
      (Boolean(session.user) && !postsComposer.startup.initialFeedResolved));
  const startupOverlayPhase = !session.authResolved ? "session" : "feed";
  const skipAutomaticScrollReset = shouldSkipAutomaticScrollReset(location);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (skipAutomaticScrollReset) return undefined;

    resetAppScrollContainers();

    const frameId = window.requestAnimationFrame(resetAppScrollContainers);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [
    location.hash,
    location.pathname,
    location.search,
    skipAutomaticScrollReset,
    session.profileUi.showProfile,
    session.profileUi.showUserProfile,
  ]);

  useEffect(() => {
    setCanViewAdminAnalytics(false);

    if (!session.user?.id || typeof window === "undefined") {
      return undefined;
    }

    const controller = new AbortController();
    getAdminAnalyticsAccess({ signal: controller.signal })
      .then((payload) => {
        if (!controller.signal.aborted && payload?.canAccess === true) {
          setCanViewAdminAnalytics(true);
        }
      })
      .catch((error) => {
        if (error?.name !== "AbortError" && !controller.signal.aborted) {
          setCanViewAdminAnalytics(false);
        }
      });

    return () => controller.abort();
  }, [session.user?.id]);

  useEffect(() => {
    if (!session.user?.id) {
      lastTrackedPageEventRef.current = "";
      return;
    }

    let analyticsConfig = null;
    if (location.pathname === "/" && activeTab === "feed") {
      analyticsConfig = ROOT_TAB_OPEN_ANALYTICS.feed;
    } else if (location.pathname === "/" && activeTab === "map") {
      analyticsConfig = ROOT_TAB_OPEN_ANALYTICS.map;
    } else if (location.pathname === "/" && activeTab === "groups") {
      analyticsConfig = ROOT_TAB_OPEN_ANALYTICS.groups;
    } else if (location.pathname === "/" && activeTab === "market") {
      analyticsConfig = ROOT_TAB_OPEN_ANALYTICS.market;
    } else {
      analyticsConfig = PAGE_OPEN_ANALYTICS_BY_PATH[location.pathname] || null;
    }

    if (location.pathname === "/fishing-today" && !canAccessFishingOutlook) {
      analyticsConfig = null;
    }

    if (!analyticsConfig) {
      lastTrackedPageEventRef.current = "";
      return;
    }

    const [eventName, featureArea] = analyticsConfig;
    const pagePath = `${location.pathname}${location.search}`;
    const trackingKey = `${eventName}:${pagePath}`;
    if (lastTrackedPageEventRef.current === trackingKey) {
      return;
    }

    lastTrackedPageEventRef.current = trackingKey;
    trackProductEvent(eventName, {
      featureArea,
      pagePath,
      metadata: { route: pagePath },
    });
  }, [
    activeTab,
    canAccessFishingOutlook,
    location.pathname,
    location.search,
    session.user?.id,
  ]);

  useEffect(() => {
    if (!session.user?.id) {
      profileOpenTrackedRef.current = false;
      return;
    }

    if (session.profileUi.showProfile && !profileOpenTrackedRef.current) {
      profileOpenTrackedRef.current = true;
      const profilePagePath =
        profileOpenPagePathOverrideRef.current ||
        `${location.pathname}${location.search}`;

      trackProductEvent("profile_opened", {
        featureArea: "profile",
        pagePath: profilePagePath,
        metadata: { source: "own_profile" },
      });
    }

    if (!session.profileUi.showProfile) {
      profileOpenTrackedRef.current = false;
      profileOpenPagePathOverrideRef.current = "";
    }
  }, [
    location.pathname,
    location.search,
    session.profileUi.showProfile,
    session.user?.id,
  ]);
  const isHomeShellRoute = Boolean(session.user) && location.pathname === "/";
  const isModernPageShellRoute =
    Boolean(session.user) && MODERN_PAGE_SHELL_ROUTES.has(location.pathname);
  const isReelitProShellRoute = isHomeShellRoute || isModernPageShellRoute;
  const shouldRenderGlobalChrome = isReelitProShellRoute;
  const useDesktopMapRightPanel =
    isHomeShellRoute && !isMobileViewport && activeTab === "map";
  const isProfileDesktopShell =
    Boolean(session.user) &&
    !isMobileViewport &&
    session.profileUi.showProfile &&
    !session.profileUi.showUserProfile;
  const isUserProfileDesktopShell =
    Boolean(session.user) &&
    !isMobileViewport &&
    session.profileUi.showUserProfile &&
    Boolean(session.profileUi.selectedUser);
  const homeShellClassName = isHomeShellRoute
    ? `rp-shell rp-home-shell rp-home-shell--tab-${activeTab}`
    : isModernPageShellRoute
    ? `rp-shell rp-home-shell rp-page-shell rp-page-shell--${location.pathname
        .replace(/^\//, "")
        .replace(/[/]+/g, "-")}${
        MOBILE_SEARCH_HIDDEN_ROUTES.has(location.pathname)
          ? " rp-page-shell--mobile-search-hidden"
          : ""
      }`
    : "";

  const closeSidebar = useCallback(() => {
    setShowSidebar(false);
  }, []);
  const handleMapRightPanelActiveChange = useCallback((isActive) => {
    setMapRightPanelActive(Boolean(isActive));
  }, []);
  const handleMapLocateMeHandlerChange = useCallback((handler) => {
    setMapLocateMeHandler(() =>
      typeof handler === "function" ? handler : null
    );
  }, []);
  const handleCatchMarkerDetailsOpenChange = useCallback((isOpen) => {
    setIsCatchMarkerDetailsOpen(Boolean(isOpen));
  }, []);

  useEffect(() => {
    if (!useDesktopMapRightPanel) {
      setMapRightPanelActive(false);
    }
  }, [useDesktopMapRightPanel]);

  const handleProfileShellNavigateAway = useCallback(() => {
    session.profileUi.setShowProfile(false);
    session.profileUi.setShowUserProfile(false);
    session.profileUi.setSelectedUser(null);
  }, [session.profileUi]);
  const clearSidebarMapSelectionState = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    MapStateManager.clearOverlay();
    window.dispatchEvent(new Event(CLEAR_MAP_SELECTION_EVENT));
  }, []);
  const handleRootTabChange = useCallback(
    (nextTab) => {
      overlayHistoryRef.current?.requestSuppressNavigationSync?.();
      session.search.setSearchQuery("");
      setShowSidebar(false);
      handleProfileShellNavigateAway();
      setActiveTab(nextTab);
    },
    [handleProfileShellNavigateAway, session.search, setActiveTab]
  );
  const handleSidebarRootTabChange = useCallback(
    (nextTab) => {
      if (normalizeRootTab(nextTab) === "map") {
        clearSidebarMapSelectionState();
      }

      handleRootTabChange(nextTab);
    },
    [clearSidebarMapSelectionState, handleRootTabChange]
  );
  const handleHomeShellTabChange = useCallback(
    (nextTab) => {
      handleRootTabChange(nextTab);
    },
    [handleRootTabChange]
  );
  const handleSidebarRouteNavigate = useCallback(
    (path) => {
      const normalizedPath = String(path || "").trim();
      if (!normalizedPath.startsWith("/")) {
        setShowSidebar(false);
        return;
      }

      overlayHistoryRef.current?.requestSuppressNavigationSync?.();
      session.search.setSearchQuery("");
      setShowSidebar(false);
      handleProfileShellNavigateAway();
      if (location.pathname === "/trips" && normalizedPath !== "/trips") {
        navigate(normalizedPath, {
          state: removeTripsOverlayStackFromState(location.state),
        });
        return;
      }

      navigate(normalizedPath);
    },
    [handleProfileShellNavigateAway, location.pathname, location.state, navigate, session.search]
  );
  const handleProfileShellTabChange = useCallback(
    (nextTab) => {
      handleRootTabChange(nextTab);
    },
    [handleRootTabChange]
  );
  const handleOpenOwnProfileFromSharedSidebar = useCallback(() => {
    if (
      location.pathname === "/trips" ||
      location.pathname.startsWith("/trips/")
    ) {
      overlayHistoryRef.current?.requestSuppressNavigationSync?.();
      activeTabRef.current = "feed";
      setActiveTabState("feed");
      profileOpenPagePathOverrideRef.current = "/";
      const destinationState = buildLocationStackState(
        removeTripsOverlayStackFromState(location.state),
        "appOverlayStack",
        ["profile"]
      );
      navigate(
        {
          pathname: "/",
          search: "",
          hash: "",
        },
        {
          replace: true,
          state: destinationState,
          flushSync: true,
        }
      );
    }

    setShowSidebar(false);
    setShowMenu(false);
    session.profileUi.setSelectedUser(null);
    session.profileUi.setShowUserProfile(false);
    session.profileUi.setShowProfile(true);
  }, [location.pathname, location.state, navigate, session.profileUi]);
  const openDeleteAccountConfirm = useCallback(() => {
    setDeleteAccountError("");
    setShowDeleteAccountConfirm(true);
  }, []);
  const closeDeleteAccountConfirm = useCallback(() => {
    if (isDeletingAccount) return;
    setDeleteAccountError("");
    setShowDeleteAccountConfirm(false);
  }, [isDeletingAccount]);
  const confirmDeleteAccount = useCallback(async () => {
    if (isDeletingAccount) return;

    setDeleteAccountError("");
    setIsDeletingAccount(true);
    try {
      await session.actions.handleDeleteAccount();
      setShowDeleteAccountConfirm(false);
    } catch (error) {
      setDeleteAccountError(
        error?.message ||
          t("delete_account_failed", {
            defaultValue: "We couldn't delete your account right now.",
          })
      );
    } finally {
      setIsDeletingAccount(false);
    }
  }, [isDeletingAccount, session.actions, t]);
  const openLogoutConfirm = useCallback(() => {
    setShowLogoutConfirm(true);
  }, []);
  const closeLogoutConfirm = useCallback(() => {
    setShowLogoutConfirm(false);
  }, []);
  const confirmLogout = useCallback(() => {
    setShowLogoutConfirm(false);
    session.actions.handleLogout();
  }, [session.actions]);
  const renderReelitProNavBanner = ({ setProfileVisible } = {}) => (
    <NavBanner
      currentUser={session.user}
      useReelitProChrome={true}
      activeTab={activeTab}
      setShowMenu={setShowMenu}
      showMenu={showMenu}
      setShowProfile={setProfileVisible || handleOpenOwnProfileFromSharedSidebar}
      authTransitioning={session.isAuthTransitioning}
      onRequestDeleteAccount={openDeleteAccountConfirm}
      onRequestLogout={openLogoutConfirm}
      onLogout={session.actions.handleLogout}
      searchQuery={session.search.searchQuery}
      setSearchQuery={session.search.setSearchQuery}
      searchResults={session.search.searchResults}
      onSelectSearchUser={session.actions.handleOpenUserFromSearch}
      groupSearchResults={session.search.groupSearchResults}
      onSelectSearchGroup={session.actions.handleOpenGroupFromSearch}
      onOpenNotificationPost={postsComposer.actions.handleOpenPostFromNotification}
      setShowInbox={messaging.setShowInbox}
      onOpenMessagesInbox={messaging.handleOpenInbox}
      setSidebarOpen={setShowSidebar}
      onMapLocateMe={mapLocateMeHandler}
      isCatchMarkerDetailsOpen={isCatchMarkerDetailsOpen}
      appMode={appMode}
      onAppModeChange={handleAppModeChange}
      themeMode={themeMode}
      onThemeModeChange={handleThemeModeChange}
    />
  );
  const profileDesktopShellNavbar =
    isProfileDesktopShell || isUserProfileDesktopShell ? (
      <div className="rp-shell rp-home-shell">
        {renderReelitProNavBanner({
          setProfileVisible: handleOpenOwnProfileFromSharedSidebar,
        })}
      </div>
    ) : null;
  const profileDesktopShellSidebar = isProfileDesktopShell ? (
    <div className="rp-shell rp-home-shell rp-profile-desktop-sidebar-flow">
      <Sidebar
        renderInShell={true}
        shellVariant="profile"
        currentUser={session.user}
        setSidebarOpen={setShowSidebar}
        activeTab={activeTab}
        onRootTabSelect={handleSidebarRootTabChange}
        onOpenProfile={handleOpenOwnProfileFromSharedSidebar}
        onSidebarRouteNavigate={handleSidebarRouteNavigate}
        onRequestDeleteAccount={openDeleteAccountConfirm}
        onRequestLogout={openLogoutConfirm}
        onLogout={session.actions.handleLogout}
        onOpenCreatePost={postsComposer.composer.setShowCreatePost}
        canViewAdminAnalytics={canViewAdminAnalytics}
        canAccessFishingOutlook={canAccessFishingOutlook}
        appMode={appMode}
        isSimpleModeActive={simpleModeActive}
        onAppModeChange={handleAppModeChange}
        themeMode={themeMode}
        onThemeModeChange={handleThemeModeChange}
      />
    </div>
  ) : null;
  const userProfileDesktopShellSidebar = isUserProfileDesktopShell ? (
    <div className="rp-shell rp-home-shell rp-user-profile-desktop-sidebar-flow">
      <Sidebar
        renderInShell={true}
        shellVariant="user-profile"
        currentUser={session.user}
        setSidebarOpen={setShowSidebar}
        activeTab={activeTab}
        onRootTabSelect={handleSidebarRootTabChange}
        onOpenProfile={handleOpenOwnProfileFromSharedSidebar}
        onSidebarRouteNavigate={handleSidebarRouteNavigate}
        onRequestDeleteAccount={openDeleteAccountConfirm}
        onRequestLogout={openLogoutConfirm}
        onLogout={session.actions.handleLogout}
        onOpenCreatePost={postsComposer.composer.setShowCreatePost}
        canViewAdminAnalytics={canViewAdminAnalytics}
        canAccessFishingOutlook={canAccessFishingOutlook}
        appMode={appMode}
        isSimpleModeActive={simpleModeActive}
        onAppModeChange={handleAppModeChange}
        themeMode={themeMode}
        onThemeModeChange={handleThemeModeChange}
      />
    </div>
  ) : null;
  const profileDesktopShellRightPanel = isProfileDesktopShell ? (
    <div className="rp-shell rp-home-shell rp-profile-desktop-rightpanel-flow">
      <FeedDesktopRightPanel
        currentUser={session.user}
        posts={postsComposer.posts}
        users={session.search.searchResults}
        onOpenUser={session.actions.handleOpenUserFromSearch}
      />
    </div>
  ) : null;
  const userProfileDesktopShellRightPanel = isUserProfileDesktopShell ? (
    <div className="rp-shell rp-home-shell rp-user-profile-desktop-rightpanel-flow">
      <FeedDesktopRightPanel
        currentUser={session.user}
        posts={postsComposer.posts}
        users={session.search.searchResults}
        onOpenUser={session.actions.handleOpenUserFromSearch}
      />
    </div>
  ) : null;
  const closeCreatePost = useCallback(() => {
    postsComposer.actions.handleCreatePostClose();
  }, [postsComposer.actions]);
  const closeInfoPopup = useCallback(() => {
    postsComposer.info.setShowInfo(false);
  }, [postsComposer.info]);
  const closeOwnProfile = useCallback(() => {
    session.profileUi.setShowProfile(false);
  }, [session.profileUi]);
  const closeUserProfile = useCallback(() => {
    session.profileUi.setShowUserProfile(false);
  }, [session.profileUi]);
  const closeSharePost = useCallback(() => {
    postsComposer.sharing.setSharePost(null);
  }, [postsComposer.sharing]);
  const closeLevelUp = useCallback(() => {
    postsComposer.levelUp.setLevelUpData(null);
  }, [postsComposer.levelUp]);

  const appOverlayHistoryItems = useMemo(
    () => [
      {
        id: "sidebar",
        isOpen: showSidebar,
        close: closeSidebar,
      },
      {
        id: "info-popup",
        isOpen: postsComposer.info.showInfo && Boolean(postsComposer.info.selectedPost),
        close: closeInfoPopup,
      },
      {
        id: "create-post",
        isOpen: postsComposer.composer.showCreatePost && Boolean(session.user),
        close: closeCreatePost,
      },
      {
        id: "profile",
        isOpen: session.profileUi.showProfile,
        close: closeOwnProfile,
      },
      {
        id: "user-profile",
        isOpen:
          session.profileUi.showUserProfile &&
          Boolean(session.profileUi.selectedUser),
        close: closeUserProfile,
      },
      {
        id: "share-post",
        isOpen: Boolean(postsComposer.sharing.sharePost),
        close: closeSharePost,
      },
      {
        id: "level-up",
        isOpen: Boolean(postsComposer.levelUp.levelUpData),
        close: closeLevelUp,
      },
    ],
    [
      closeCreatePost,
      closeInfoPopup,
      closeLevelUp,
      closeOwnProfile,
      closeSharePost,
      closeSidebar,
      closeUserProfile,
      postsComposer.composer.showCreatePost,
      postsComposer.info.selectedPost,
      postsComposer.info.showInfo,
      postsComposer.levelUp.levelUpData,
      postsComposer.sharing.sharePost,
      session.profileUi.selectedUser,
      session.profileUi.showProfile,
      session.profileUi.showUserProfile,
      session.user,
      showSidebar,
    ]
  );

  const pendingAppFishipediaRestore = getFishipediaRestoreContext(
    location.state
  );
  const overlayHistory = useAppOverlayHistory(appOverlayHistoryItems, {
    deferSynchronization:
      (location.pathname === "/" &&
        pendingAppFishipediaRestore?.type === "post-info" &&
        pendingAppFishipediaRestore.surface === "app") ||
      isFishipediaHistoryTransitionPending("appOverlayStack"),
  });
  overlayHistoryRef.current = overlayHistory;
  suppressPickerOpenNavigationRef.current =
    overlayHistory.requestSuppressNavigationSync;
  suppressPickerReturnNavigationRef.current =
    overlayHistory.requestSuppressNavigationSync;

  const requestAppChromeMetricsRefresh = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    const dispatchRefresh = () => {
      window.dispatchEvent(new Event(REFRESH_APP_CHROME_METRICS_EVENT));
    };

    dispatchRefresh();

    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(dispatchRefresh);
    }

    window.setTimeout(dispatchRefresh, 80);
  }, []);

  const resetMobileViewportScrollToTop = useCallback(() => {
    if (!isMobileViewport || typeof window === "undefined") {
      return;
    }

    const resetScroll = () => {
      const doc = window.document;
      const scrollingElement = doc.scrollingElement || doc.documentElement;

      try {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } catch (error) {
        window.scrollTo(0, 0);
      }

      if (scrollingElement) {
        scrollingElement.scrollTop = 0;
        scrollingElement.scrollLeft = 0;
      }

      if (doc.body) {
        doc.body.scrollTop = 0;
        doc.body.scrollLeft = 0;
      }
    };

    resetScroll();

    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(resetScroll);
    }

    window.setTimeout(resetScroll, 80);
  }, [isMobileViewport]);

  const handleInfoNavigateAway = useCallback((destination) => {
    if (!destination || typeof destination !== "object") {
      return;
    }

    overlayHistory.requestSuppressNavigationSync();

    if (destination.type === "fishipedia" && destination.slug) {
      const returnContext = createPostInfoFishipediaReturnContext({
        post: postsComposer.info.selectedPost,
        historyLocation: location,
      });
      if (!returnContext) return;
      const fishipediaState = returnContext.historyTransaction
        ? { [FISHIPEDIA_RETURN_CONTEXT_KEY]: returnContext }
        : null;

      postsComposer.info.setShowInfo(false);
      session.profileUi.setShowProfile(false);
      session.profileUi.setShowUserProfile(false);

      // Reuse the active child slot so its genuine base entry remains directly
      // behind Fishipedia and can be restored without an empty history layer.
      navigate(`/fishipedia#${destination.slug}`, {
        replace: true,
        state: fishipediaState,
      });
      return;
    }

    if (destination.type === "map") {
      const { lat, lng } = destination;

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return;
      }

      const pendingLocation = { lat, lng };

      if (Number.isFinite(destination.zoom)) {
        pendingLocation.zoom = destination.zoom;
      }

      if (destination.openOverlay === false) {
        pendingLocation.openOverlay = false;
      }

      try {
        sessionStorage.setItem(
          "pendingMapLocation",
          JSON.stringify(pendingLocation)
        );
      } catch (error) {
        console.warn("Unable to persist pending map location", error);
      }
    }

    postsComposer.info.setShowInfo(false);
    session.profileUi.setShowProfile(false);
    session.profileUi.setShowUserProfile(false);

    if (destination.type === "map") {
      resetMobileViewportScrollToTop();
      handleHomeShellTabChange("map");
      requestAppChromeMetricsRefresh();
      return;
    }

  }, [
    handleHomeShellTabChange,
    location,
    navigate,
    overlayHistory,
    postsComposer.info,
    requestAppChromeMetricsRefresh,
    resetMobileViewportScrollToTop,
    session.profileUi,
  ]);

  const hasOpenAppOverlay = appOverlayHistoryItems.some((item) => item.isOpen);
  const isAtTrueRoot =
    Boolean(session.user) &&
    location.pathname === "/" &&
    activeTab === "feed" &&
    buildRootTabSearch(location.search, "feed") === "" &&
    !showMenu &&
    !hasOpenAppOverlay;
  const { isSupportedContext: isAndroidRootExitContext, showPrompt: showRootExitPrompt } =
    useAndroidRootExitGuard({
      isAtTrueRoot,
    });

  useEffect(() => {
    if (!showSidebar) return;
    if (typeof window === "undefined" || window.innerWidth > 768) return;

    const handlePointerDownOutsideSidebar = (event) => {
      const clickedInsideSidebar = event.target.closest?.(".feed-sidebar");
      const clickedBurgerButton = event.target.closest?.(".mobile-menu-btn");

      if (!clickedInsideSidebar && !clickedBurgerButton) {
        setShowSidebar(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDownOutsideSidebar);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDownOutsideSidebar);
    };
  }, [showSidebar]);

  if (!session.authResolved) {
    return shouldShowLoggedInStartupOverlay ? (
      <AppStartupOverlay phase={startupOverlayPhase} />
    ) : null;
  }

  return (
    <PushRegistrationLifecycleProvider
      accountKey={session.user?.id || session.user?.username || null}
      authResolved={session.authResolved}
      lifecycleRef={pushLifecycleRef}
    >
      <>
      {session.user && !session.isAuthTransitioning ? (
        <PushNotificationOnboarding
          accountKey={session.user?.id || session.user?.username || null}
          currentUser={session.user}
          appMode={appMode}
          onAppModeChange={handleAppModeChange}
          themeMode={themeMode}
          onThemeModeChange={handleThemeModeChange}
        />
      ) : null}
      {!session.user ? (
        <AppRoutes
          t={t}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setShowSidebar={setShowSidebar}
          onRootTabSelect={handleSidebarRootTabChange}
          onSidebarRouteNavigate={handleSidebarRouteNavigate}
          session={session}
          postsComposer={postsComposer}
          messaging={messaging}
          normalizeCoords={normalizeCoords}
          fishingOutlookAccessState={fishingOutlookAccessState}
          onCatchMarkerDetailsOpenChange={handleCatchMarkerDetailsOpenChange}
          appMode={appMode}
          isSimpleModeActive={simpleModeActive}
          onSwitchToFullMode={handleSwitchToFullMode}
        />
      ) : (
        <div
          className={`app-container ${
            session.profileUi.showUserProfile ? "userprofile-open" : ""
          } ${showSidebar ? "sidebar-open" : ""} ${
            isProfileDesktopShell ? "rp-profile-desktop-shell-open" : ""
          } ${
            isUserProfileDesktopShell ? "rp-user-profile-desktop-shell-open" : ""
          } ${homeShellClassName}`.trim()}
        >
          <div className={isReelitProShellRoute ? "rp-home-shell__frame" : undefined}>
            {shouldRenderGlobalChrome &&
            !isProfileDesktopShell &&
            !isUserProfileDesktopShell ? (
              renderReelitProNavBanner()
            ) : null}

            {isReelitProShellRoute ? (
              <div
                className={`rp-desktop${
                  isModernPageShellRoute ? " rp-page-shell__desktop" : ""
                }`}
              >
                <div
                  className={`rp-desktop__body${
                    isModernPageShellRoute ? " rp-page-shell__body" : ""
                  }`}
                >
                  <Sidebar
                    renderInShell={true}
                    shellVariant="home"
                    isOpen={
                      showSidebar &&
                      !session.profileUi.showProfile &&
                      !session.profileUi.showUserProfile
                    }
                    onClose={closeSidebar}
                    currentUser={session.user}
                    showUserProfile={session.profileUi.showUserProfile}
                    showProfile={session.profileUi.showProfile}
                    setSidebarOpen={setShowSidebar}
                    activeTab={activeTab}
                    onRootTabSelect={handleSidebarRootTabChange}
                    onOpenProfile={handleOpenOwnProfileFromSharedSidebar}
                    onSidebarRouteNavigate={handleSidebarRouteNavigate}
                    onRequestDeleteAccount={openDeleteAccountConfirm}
                    onRequestLogout={openLogoutConfirm}
                    onLogout={session.actions.handleLogout}
                    onOpenCreatePost={postsComposer.composer.setShowCreatePost}
                    canViewAdminAnalytics={canViewAdminAnalytics}
                    canAccessFishingOutlook={canAccessFishingOutlook}
                    appMode={appMode}
                    isSimpleModeActive={simpleModeActive}
                    onAppModeChange={handleAppModeChange}
                    themeMode={themeMode}
                    onThemeModeChange={handleThemeModeChange}
                  />

                  <main
                    className={`content rp-desktop__main${
                      isModernPageShellRoute ? " rp-page-shell__main" : ""
                    }`}
                  >
                    <div
                      className={`rp-desktop__feed-inner${
                        isModernPageShellRoute
                          ? " rp-page-shell__content"
                          : ""
                      }`}
                    >
                      <AppRoutes
                        t={t}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        setShowSidebar={setShowSidebar}
                        session={session}
                        postsComposer={postsComposer}
                        messaging={messaging}
                        normalizeCoords={normalizeCoords}
                        fishingOutlookAccessState={fishingOutlookAccessState}
                        useDesktopMapRightPanel={useDesktopMapRightPanel}
                        mapRightPanelTargetId={MAP_RIGHT_PANEL_HOST_ID}
                        tripsRightPanelTargetId={TRIPS_RIGHT_PANEL_HOST_ID}
                        utilityRightPanelTargetId={UTILITY_RIGHT_PANEL_HOST_ID}
                        onMapRightPanelActiveChange={handleMapRightPanelActiveChange}
                        onMapLocateMeHandlerChange={handleMapLocateMeHandlerChange}
                        onCatchMarkerDetailsOpenChange={handleCatchMarkerDetailsOpenChange}
                        onRootTabSelect={handleSidebarRootTabChange}
                        onSidebarRouteNavigate={handleSidebarRouteNavigate}
                        useModernExploreShell={isModernPageShellRoute}
                        appMode={appMode}
                        isSimpleModeActive={simpleModeActive}
                        onSwitchToFullMode={handleSwitchToFullMode}
                      />
                    </div>
                  </main>

                  {isModernPageShellRoute &&
                  location.pathname === "/trips" &&
                  !isMobileViewport ? (
                    <aside
                      id={TRIPS_RIGHT_PANEL_HOST_ID}
                      className="rp-trips-rightpanel-host"
                    />
                  ) : null}

                  {isModernPageShellRoute &&
                  (location.pathname === "/my-gear" ||
                    location.pathname === "/favorite-spots" ||
                    location.pathname === "/suggested-anglers" ||
                    location.pathname === "/fishipedia" ||
                    location.pathname === "/fishipedia/rankings" ||
                    location.pathname === "/feedback" ||
                    location.pathname === "/manual" ||
                    location.pathname === "/surveys") &&
                  !isMobileViewport ? (
                    <aside
                      id={UTILITY_RIGHT_PANEL_HOST_ID}
                      className="rp-utility-rightpanel-host"
                    />
                  ) : null}

                  {isHomeShellRoute &&
                  !isMobileViewport &&
                  (!simpleModeActive || activeTab === "map") ? (
                    activeTab === "map" ? (
                      <div
                        className={`rp-map-rightpanel-slot${
                          mapRightPanelActive
                            ? " rp-map-rightpanel-slot--active"
                            : ""
                        }`}
                      >
                        <div
                          id={MAP_RIGHT_PANEL_HOST_ID}
                          className="rp-map-rightpanel-host"
                        />

                        {!mapRightPanelActive ? (
                          <FeedDesktopRightPanel
                            currentUser={session.user}
                            posts={postsComposer.posts}
                            users={session.search.searchResults}
                            onOpenUser={session.actions.handleOpenUserFromSearch}
                          />
                        ) : null}
                      </div>
                    ) : (
                      <FeedDesktopRightPanel
                        currentUser={session.user}
                        posts={postsComposer.posts}
                        users={session.search.searchResults}
                        onOpenUser={session.actions.handleOpenUserFromSearch}
                      />
                    )
                  ) : null}
                </div>

                {isMobileViewport &&
                !session.profileUi.showProfile &&
                !(
                  session.profileUi.showUserProfile &&
                  session.profileUi.selectedUser
                ) ? (
                  <FeedMobileBottomNav
                    activeTab={activeTab}
                    onTabSelect={handleHomeShellTabChange}
                    onRouteNavigate={handleSidebarRouteNavigate}
                    canAccessFishingOutlook={canAccessFishingOutlook}
                    isSimpleModeActive={simpleModeActive}
                  />
                ) : null}
              </div>
            ) : (
              <main className="content">
                <AppRoutes
                  t={t}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  setShowSidebar={setShowSidebar}
                  onRootTabSelect={handleSidebarRootTabChange}
                  onSidebarRouteNavigate={handleSidebarRouteNavigate}
                  session={session}
                  postsComposer={postsComposer}
                  messaging={messaging}
                  normalizeCoords={normalizeCoords}
                  fishingOutlookAccessState={fishingOutlookAccessState}
                  onCatchMarkerDetailsOpenChange={handleCatchMarkerDetailsOpenChange}
                  appMode={appMode}
                  isSimpleModeActive={simpleModeActive}
                  onSwitchToFullMode={handleSwitchToFullMode}
                />
              </main>
            )}
          </div>

          {postsComposer.info.showInfo && postsComposer.info.selectedPost && (
            <InfoPopup
              post={{
                ...postsComposer.info.selectedPost,
                catch: {
                  ...postsComposer.info.selectedPost.catch,
                  coordinates: normalizeCoords(
                    postsComposer.info.selectedPost.catch?.coordinates
                  ),
                },
              }}
              posts={postsComposer.posts}
              viewer={session.user}
              enableFishipediaFishLink={true}
              onNavigateAway={handleInfoNavigateAway}
              onClose={closeInfoPopup}
            />
          )}

          {postsComposer.composer.showCreatePost && session.user && (
            <div
              className="modal-overlay"
              onClick={(event) => {
                if (event.target !== event.currentTarget) {
                  return;
                }

                if (typeof window !== "undefined") {
                  window.dispatchEvent(
                    new CustomEvent("reelitpro:create-post-close-request", {
                      detail: { reason: "backdrop" },
                    })
                  );
                } else {
                  postsComposer.actions.handleCreatePostOverlayClick(event);
                }
              }}
            >
              <CreatePostModal
                user={session.user}
                onUserUpdated={session.setUser}
                isSimpleModeActive={simpleModeActive}
                isSubmitting={postsComposer.composer.isCreatePostSubmitting}
                onSubmittingChange={
                  postsComposer.composer.setIsCreatePostSubmitting
                }
                pickedCoordinates={postsComposer.composer.pickedCoordinates}
                draftCatchMedia={postsComposer.composer.draftCatchMedia}
                setDraftCatchMedia={postsComposer.composer.setDraftCatchMedia}
                draftCatchFormState={postsComposer.composer.draftCatchFormState}
                setDraftCatchFormState={
                  postsComposer.composer.setDraftCatchFormState
                }
                draftExifDate={postsComposer.composer.draftExifDate}
                setDraftExifDate={postsComposer.composer.setDraftExifDate}
                draftExifTime={postsComposer.composer.draftExifTime}
                setDraftExifTime={postsComposer.composer.setDraftExifTime}
                onClose={postsComposer.actions.handleCreatePostClose}
                onAddPost={postsComposer.actions.handleAddPost}
                favoriteSpots={session.favorites.favoriteSpots}
                showFavorites={session.favorites.showFavorites}
                setShowFavorites={session.favorites.setShowFavorites}
                setActiveTab={setActiveTab}
                setShowProfile={session.profileUi.setShowProfile}
              />
            </div>
          )}

          {session.profileUi.showProfile && (
            <Profile
              user={session.user}
              users={session.search.searchResults}
              isOwnProfile={true}
              bioEditRequest={session.profileUi.profileBioEditRequest}
              isBioEditRequestReady={Boolean(
                location.state?.appOverlayStack?.includes("profile")
              )}
              onBioEditRequestConsumed={
                session.profileUi.consumeProfileBioEditRequest
              }
              focusPostRequest={session.profileUi.profileFocusPostRequest}
              favoriteSpots={session.favorites.favoriteSpots}
              showFavorites={session.favorites.showFavorites}
              setShowFavorites={session.favorites.setShowFavorites}
              setActiveTab={setActiveTab}
              setShowProfile={session.profileUi.setShowProfile}
              setSelectedPost={postsComposer.info.setSelectedPost}
              setShowInfo={postsComposer.info.setShowInfo}
              setSharePost={postsComposer.sharing.setSharePost}
              onDeletePost={postsComposer.actions.handleDeletePost}
              onUpdatePost={postsComposer.actions.handleUpdatePost}
              editPostMapSelection={postsComposer.composer.editPostMapSelection}
              onEditPostMapSelectionConsumed={
                postsComposer.composer.clearEditPostMapSelection
              }
              onOpenSharedSidebar={() => setShowSidebar(true)}
              desktopShellNavbar={profileDesktopShellNavbar}
              desktopShellSidebar={profileDesktopShellSidebar}
              desktopShellRightPanel={profileDesktopShellRightPanel}
            />
          )}

          {session.profileUi.showUserProfile &&
            session.profileUi.selectedUser && (
              <UserProfile
                user={session.user}
                selectedUser={session.profileUi.selectedUser}
                focusPostRequest={
                  session.profileUi.userProfileFocusPostRequest
                }
                setShowUserProfile={session.profileUi.setShowUserProfile}
                setSelectedPost={postsComposer.info.setSelectedPost}
                setShowInfo={postsComposer.info.setShowInfo}
                setSharePost={postsComposer.sharing.setSharePost}
                setChatUserId={messaging.setChatUserId}
                onOpenSharedSidebar={() => setShowSidebar(true)}
                desktopShellNavbar={profileDesktopShellNavbar}
                desktopShellSidebar={userProfileDesktopShellSidebar}
                desktopShellRightPanel={userProfileDesktopShellRightPanel}
              />
            )}

          {isMobileViewport &&
            session.profileUi.showProfile &&
            !session.profileUi.showUserProfile && (
              <div className="rp-shell rp-home-shell rp-profile-drawer-host">
                <Sidebar
                  renderInShell={true}
                  shellVariant="profile"
                  isOpen={showSidebar}
                  onClose={closeSidebar}
                  currentUser={session.user}
                  setSidebarOpen={setShowSidebar}
                  activeTab={activeTab}
                  onRootTabSelect={handleSidebarRootTabChange}
                  onOpenProfile={handleOpenOwnProfileFromSharedSidebar}
                  onSidebarRouteNavigate={handleSidebarRouteNavigate}
                  onRequestDeleteAccount={openDeleteAccountConfirm}
                  onRequestLogout={openLogoutConfirm}
                  onLogout={session.actions.handleLogout}
                  onOpenCreatePost={postsComposer.composer.setShowCreatePost}
                  canViewAdminAnalytics={canViewAdminAnalytics}
                  canAccessFishingOutlook={canAccessFishingOutlook}
                  appMode={appMode}
                  isSimpleModeActive={simpleModeActive}
                  onAppModeChange={handleAppModeChange}
                  themeMode={themeMode}
                  onThemeModeChange={handleThemeModeChange}
                />
              </div>
            )}

          {isMobileViewport &&
            session.profileUi.showUserProfile &&
            session.profileUi.selectedUser && (
              <div className="rp-shell rp-home-shell rp-user-profile-drawer-host">
                <Sidebar
                  renderInShell={true}
                  shellVariant="user-profile"
                  isOpen={showSidebar}
                  onClose={closeSidebar}
                  currentUser={session.user}
                  setSidebarOpen={setShowSidebar}
                  activeTab={activeTab}
                  onRootTabSelect={handleSidebarRootTabChange}
                  onOpenProfile={handleOpenOwnProfileFromSharedSidebar}
                  onSidebarRouteNavigate={handleSidebarRouteNavigate}
                  onRequestDeleteAccount={openDeleteAccountConfirm}
                  onRequestLogout={openLogoutConfirm}
                  onLogout={session.actions.handleLogout}
                  onOpenCreatePost={postsComposer.composer.setShowCreatePost}
                  canViewAdminAnalytics={canViewAdminAnalytics}
                  canAccessFishingOutlook={canAccessFishingOutlook}
                  appMode={appMode}
                  isSimpleModeActive={simpleModeActive}
                  onAppModeChange={handleAppModeChange}
                  themeMode={themeMode}
                  onThemeModeChange={handleThemeModeChange}
                />
              </div>
            )}

          {isMobileViewport &&
            (session.profileUi.showProfile ||
              (session.profileUi.showUserProfile &&
                session.profileUi.selectedUser)) && (
              <div className="rp-shell rp-home-shell rp-profile-bottom-nav-host">
                <FeedMobileBottomNav
                  activeTab={activeTab}
                  onTabSelect={handleProfileShellTabChange}
                  onRouteNavigate={handleSidebarRouteNavigate}
                  canAccessFishingOutlook={canAccessFishingOutlook}
                  isSimpleModeActive={simpleModeActive}
                />
              </div>
            )}

          {postsComposer.sharing.sharePost && (
            <SharePostModal
              post={postsComposer.sharing.sharePost}
              onClose={closeSharePost}
            />
          )}

          {showDeleteAccountConfirm ? (
            <div
              className="info-popup-overlay"
              onClick={closeDeleteAccountConfirm}
            >
              <div
                className="saved-location-popup nav-account-delete-popup"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="saved-location-popup-inner nav-account-delete-popup-inner">
                  <h3>
                    {t("delete_account_title", {
                      defaultValue: "Delete account?",
                    })}
                  </h3>
                  <p>
                    {t("delete_account_warning", {
                      defaultValue:
                        "This permanently deletes your account, profile, posts, catches, saved trips, saved locations, direct messages with you, and user-owned media for the current app version.",
                    })}
                  </p>
                  <p className="nav-account-delete-warning">
                    {t("delete_account_warning_secondary", {
                      defaultValue: "This action cannot be undone.",
                    })}
                  </p>
                  <p>
                    {t("delete_account_warning_residual", {
                      defaultValue:
                        "Previously shared post snapshots inside other users' message threads may still remain until broader cleanup is added.",
                    })}
                  </p>
                  {deleteAccountError ? (
                    <p className="nav-account-delete-error">
                      {deleteAccountError}
                    </p>
                  ) : null}
                  <div className="nav-account-delete-actions">
                    <button
                      type="button"
                      className="nav-account-delete-cancel"
                      onClick={closeDeleteAccountConfirm}
                      disabled={isDeletingAccount}
                    >
                      {t("no", { defaultValue: "No" })}
                    </button>
                    <button
                      type="button"
                      className="nav-account-delete-confirm"
                      onClick={confirmDeleteAccount}
                      disabled={isDeletingAccount}
                    >
                      {isDeletingAccount
                        ? t("delete_account_loading", {
                            defaultValue: "Deleting...",
                          })
                        : t("delete_account_confirm", {
                            defaultValue: "Yes, delete account",
                          })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {showLogoutConfirm ? (
            <div className="info-popup-overlay" onClick={closeLogoutConfirm}>
              <div
                className="saved-location-popup nav-account-delete-popup"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="saved-location-popup-inner nav-account-delete-popup-inner">
                  <h3>
                    {t("logout_confirm_title", {
                      defaultValue: "Log out?",
                    })}
                  </h3>
                  <p>
                    {t("logout_confirm_message", {
                      defaultValue: "Are you sure you want to log out?",
                    })}
                  </p>
                  <div className="nav-account-delete-actions">
                    <button
                      type="button"
                      className="nav-account-delete-cancel"
                      onClick={closeLogoutConfirm}
                    >
                      {t("no", { defaultValue: "No" })}
                    </button>
                    <button
                      type="button"
                      className="nav-account-delete-confirm"
                      onClick={confirmLogout}
                    >
                      {t("yes", { defaultValue: "Yes" })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {postsComposer.levelUp.levelUpData && (
            <LevelUpModal
              level={postsComposer.levelUp.levelUpData.level}
              rankName={postsComposer.levelUp.levelUpData.rankName}
              badgeKey={postsComposer.levelUp.levelUpData.badgeKey}
              hasRankChanged={postsComposer.levelUp.levelUpData.hasRankChanged}
              onClose={closeLevelUp}
            />
          )}

          <LegalAcceptanceModal
            user={session.user}
            onAccepted={session.actions.handleLegalAcceptance}
          />

          <PostLikeErrorToast userId={session.user?.id} />

          {isAndroidRootExitContext && showRootExitPrompt && (
            <div
              className="root-exit-toast"
              role="status"
              aria-live="polite"
            >
              {t("press_back_again_to_exit", "Press back again to exit")}
            </div>
          )}
        </div>
      )}
      {shouldShowLoggedInStartupOverlay && session.user && (
        <AppStartupOverlay phase={startupOverlayPhase} />
      )}
      </>
    </PushRegistrationLifecycleProvider>
  );
}

export default function App() {
  return (
    <Router>
      <RouteSeo />
      <AppShell />
    </Router>
  );
}
